import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import UserCell from '../../components/ui/UserCell';
import StatusPill from '../../components/ui/StatusPill';
import PartnerDrawer from './PartnerDrawer';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  getPartnerViewCounts,
  listPartners,
  updateAgentStatus,
} from '../../actions/agentAction';
import { statusLabel } from '../../design/tokens';

const VIEWS = [
  { key: 'all', label: 'All partners' },
  { key: 'needs_review', label: 'Needs review', attention: true },
  { key: 'live', label: 'Live' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'blacklisted', label: 'Blacklisted' },
];

const STATUS_OPTIONS = [
  { value: 'setup', label: 'Setup' },
  { value: 'under_review', label: 'Under review' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'live', label: 'Live' },
  { value: 'blacklisted', label: 'Blacklisted' },
  { value: 'off_boarded', label: 'Off boarded' },
];

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function PartnerOnboarding() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const [drawerPartner, setDrawerPartner] = useState(null);

  const fetcher = useCallback((params) => listPartners(params), []);

  const list = useListState({
    views: VIEWS,
    fetcher,
    countsFetcher: getPartnerViewCounts,
    initialSort: { column: 'joined', direction: 'desc' },
    limit: 25,
  });

  /** Shared by the drawer footer and the bulk bar so both confirm identically. */
  const changeStatus = useCallback(
    async (partners, statusTo) => {
      const many = partners.length > 1;
      const label = statusLabel(statusTo);
      const ok = await confirm({
        title: many ? `Move ${partners.length} partners to ${label}?` : `Move to ${label}?`,
        body: many
          ? `${partners.length} partners will be set to ${label}. They are notified by email where the status change warrants it.`
          : `${partners[0].name || partners[0].email} will be set to ${label}.`,
        confirmLabel: label,
        destructive: statusTo === 'blacklisted',
      });
      if (!ok) return;

      const results = await Promise.allSettled(
        partners.map((partner) =>
          updateAgentStatus(dispatch, partner.id, {
            status_to: statusTo,
            status_from: partner.status,
            comment: `Set to ${label} from the Onboarding screen`,
          })
        )
      );

      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) {
        toast(`${results.length - failed} updated, ${failed} failed`, { tone: 'error' });
      } else {
        toast(many ? `${results.length} partners set to ${label}` : `Set to ${label}`, {
          tone: 'success',
        });
      }

      setDrawerPartner(null);
      list.clearSelection();
      list.reload();
      list.reloadCounts();
    },
    [confirm, dispatch, toast, list]
  );

  const selectedPartners = useMemo(
    () => list.items.filter((row) => list.selectedIds.has(row.id)),
    [list.items, list.selectedIds]
  );

  const columns = useMemo(
    () => [
      {
        key: 'partner',
        header: 'Partner',
        width: 260,
        sortable: true,
        render: (row) => (
          <UserCell name={row.name || 'Unnamed'} secondary={row.email} seed={row.id} />
        ),
      },
      {
        key: 'phone',
        header: 'Phone',
        width: 160,
        mono: true,
        sortable: true,
        render: (row) =>
          row.phone_number?.number
            ? `${row.phone_number.country_code || ''} ${row.phone_number.number}`.trim()
            : '—',
      },
      {
        key: 'status',
        header: 'Status',
        width: 140,
        sortable: true,
        render: (row) => <StatusPill status={row.status} />,
      },
      { key: 'bookings', header: 'Bookings', width: 110, mono: true, align: 'right' },
      {
        key: 'service_counts',
        header: 'Live / Draft / Hold',
        width: 170,
        mono: true,
        render: (row) => `${row.live_count} / ${row.under_review_count} / ${row.on_hold_count}`,
      },
      {
        key: 'joined',
        header: 'Joined',
        width: 120,
        muted: true,
        sortable: true,
        render: (row) => formatDate(row.created_at),
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        section="Partners"
        title="Onboarding"
        subtitle="Review, approve, hold or blacklist astrologers."
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(row) => row.id}
        noun="partners"
        searchPlaceholder="Name, email or phone"
        filters={[
          { key: 'status', label: 'Status', options: STATUS_OPTIONS, placeholder: 'Any status' },
          {
            key: 'bookings',
            label: 'Bookings',
            options: [
              { value: 'none', label: 'No bookings' },
              { value: 'some', label: '1 or more' },
            ],
          },
        ]}
        bulkActions={[
          {
            label: 'Approve',
            icon: 'bx-check',
            onClick: () => changeStatus(selectedPartners, 'live'),
          },
          {
            label: 'Put on hold',
            icon: 'bx-pause',
            onClick: () => changeStatus(selectedPartners, 'on_hold'),
          },
        ]}
        onRowClick={(row) => setDrawerPartner(row)}
        empty={{
          icon: 'bx-user-check',
          title: 'No partners match this view',
          description: 'Try a different saved view or clear the filters.',
        }}
      />

      <PartnerDrawer
        partner={drawerPartner}
        open={!!drawerPartner}
        onClose={() => setDrawerPartner(null)}
        onStatusChange={(partner, statusTo) => changeStatus([partner], statusTo)}
      />
    </>
  );
}
